<template>
  <div class="patient-paid-stats">
    <div class="patient-paid-stats__left">
      <div class="patient-paid-stats__group">
        <div class="patient-paid-stats__group__title">Оплачено услуг</div>
        <div class="patient-paid-stats__group__value">{{ paid }}</div>
      </div>
      <div class="patient-paid-stats__group">
        <div class="patient-paid-stats__group__title">Не оплачено</div>
        <div class="patient-paid-stats__group__value debt">{{ debt }}</div>
      </div>
      <div class="patient-paid-stats__group">
        <div class="patient-paid-stats__group__title">Аванс</div>
        <div class="patient-paid-stats__group__value">
          {{ advance }}
          <the-tooltip position="bottom">
            <template v-slot:trigger="{ listenerObject }">
              <span v-on="listenerObject">
                <button
                  type="button"
                  class="patient-paid-stats__button advance-details-open"
                  v-on:click="toAdvanceList"
                >Детализация</button>
              </span>
            </template>
            Операции с авансом
          </the-tooltip>
          <the-tooltip position="bottom">
            <template v-slot:trigger="{ listenerObject }">
              <button
                class="patient-paid-stats__button recharge"
                type="button"
                v-bind:disabled="isAwaitRecharge"
                v-on:click="onClickRecharge"
                v-on="listenerObject"
              />
            </template>
            Пополнить
          </the-tooltip>
          <the-tooltip position="bottom">
            <template v-slot:trigger="{ listenerObject }">
              <button
                class="patient-paid-stats__button withdrawal"
                type="button"
                v-bind:disabled="isAwaitRecharge"
                v-on:click="onClickWithdrawal"
                v-on="listenerObject"
              />
            </template>
            Списать
          </the-tooltip>
        </div>
      </div>
      <template v-if="patientLoyaltyCard">
        <template v-if="patientLoyaltyCard.isTypeBonusCard()">
          <bonus-card
            v-bind:bonus-card="patientLoyaltyCard"
            v-on:create-bonus-card="onOpenCreateBonusCardModal"
            v-on:open-details-bonus-card="openDetailBonusCardSidebar"
          />
        </template>
        <template v-if="patientLoyaltyCard.isTypeDiscountCard()">
          <discount-card
            v-bind:discount-card="patientLoyaltyCard"
            v-on:create-discount-card="onOpenCreateDiscountCardModal"
            v-on:open-details-discount-card="openDetailDiscountCardSidebar"
          />
        </template>
      </template>
    </div>
    <popover-menu
      v-bind:close-on-content-click="false"
      :top="false"
    >
      <template v-slot:trigger="{ listenerObject }">
        <button
          class="patient-paid-stats__act"
          type="button"
          v-bind:disabled="isAwaitForceRecharge"
          v-on="listenerObject"
        >
          Акт взаиморасчетов
        </button>
      </template>
      <form class="patient-paid-stats__act-form">
        <date-format-range-picker
          id="act-date-range"
          v-bind:value="actDateRange"
          v-on:input="onInputDateRange"
        />
        <p
          v-if="isShowInvalidActDateRangeError"
          class="patient-paid-stats__act-form__violation"
        >Укажите период</p>
        <div class="patient-paid-stats__act-form__actions">
          <button
            class="patient-paid-stats__act-form__button download"
            type="button"
            v-bind:disabled="isAwaitActRequest"
            v-on:click="onDownloadAct"
          >
            Скачать
          </button>
          <button
            class="patient-paid-stats__act-form__button print"
            type="button"
            v-bind:disabled="isAwaitActRequest"
            v-on:click="onPrintAct"
          >
            Печать
          </button>
        </div>
      </form>
    </popover-menu>
    <button
      v-if="canForceRecharge"
      class="patient-paid-stats__force-recharge"
      type="button"
      v-bind:disabled="isAwaitForceRecharge"
      v-on:click="onForcePayment"
    >
      Провести оплату
    </button>
  </div>
</template>

<script>
import PopoverMenu from '@/components/PopoverMenu.vue';
import TheTooltip from '@/components/TheTooltip.vue';
import DateFormatRangePicker from '@/components/DateFormatRangePicker.vue';
import metricMixin from '@/mixins/metricMixin';
import {
  GOAL_CLICK_PATIENT_DETAILING_ADVANCE_PATIENT_INVOICES,
  GOAL_PRINT_ACT_MUTUAL_SETTLEMENTS_PATIENT_INVOICES,
} from '@/plugins/metric';
import BonusCard from '@/components/BonusCard.vue';
import DiscountCard from '@/components/DiscountCard.vue';

/**
 * @description Название события открытия формы создания карты
 * @type {string}
 */
const NAME_EVENT_OPEN_FORM_BONUS_CARD_CREATE = 'open-modal-create-bonus-card';

/**
 * @description Название события создания скидочной карты
 * @type {string}
 */
const NAME_EVENT_OPEN_FORM_DISCOUNT_CARD_CREATE = 'open-modal-create-discount-card';

/**
 * @description Название события открытия формы пополнения.
 * @type {string}
 */
const NAME_EVENT_OPEN_RECHARGE_FORM = 'open-recharge-form';

/**
 * @description Название события открытия детализации бонусной карты.
 * @type {string}
 */
const NAME_EVENT_OPEN_DETAIL_BONUS_CARD_SIDEBAR = 'open-detail-bonus-card-sidebar';

/**
 * @description Название события открытия детализации скидочной карты.
 * @type {string}
 */
const NAME_EVENT_OPEN_DETAIL_DISCOUNT_CARD_SIDEBAR = 'open-detail-discount-card-sidebar';

/**
 * @description Принудительно пополнить аванс.
 * @type {string}
 */
const NAME_EVENT_FORCE_RECHARGE = 'force-recharge';

/**
 * @description Компонент данных оплат пациента.
 */
export default {
  components: {
    DiscountCard,
    BonusCard,
    PopoverMenu,
    TheTooltip,
    DateFormatRangePicker,
  },
  computed: {
    isInvalidActDateRange() {
      return this.actDateRange.length === 0;
    },
    navigationToAdvanceList() {
      return {
        name: 'ClinicPanelPatientAdvanceDetailList',
        params: { id: this.$route.params.id },
      };
    },
  },
  data() {
    return {
      actDateRange: [],
      isShowInvalidActDateRangeError: false,
    };
  },
  methods: {
    onInputDateRange(value) {
      this.actDateRange = value;
      this.isShowInvalidActDateRangeError = false;
    },
    /**
     * @description Обработать событие открытия модального окна выпуска бонусной карты
     * @returns {void}
     */
    onOpenCreateBonusCardModal() {
      this.$emit(NAME_EVENT_OPEN_FORM_BONUS_CARD_CREATE);
    },
    /**
     * @description Обработать событие создания скидочной карты
     * @returns {void}
     */
    onOpenCreateDiscountCardModal() {
      this.$emit(NAME_EVENT_OPEN_FORM_DISCOUNT_CARD_CREATE);
    },
    /**
     * @description К детализации карты
     * @returns void
     */
    openDetailBonusCardSidebar() {
      this.$emit(NAME_EVENT_OPEN_DETAIL_BONUS_CARD_SIDEBAR);
    },
    /**
     * @description К детализации скидочной карты
     * @returns void
     */
    openDetailDiscountCardSidebar() {
      this.$emit(NAME_EVENT_OPEN_DETAIL_DISCOUNT_CARD_SIDEBAR);
    },
    /**
     * @description К списку операций
     * @returns {void}
     */
    toAdvanceList() {
      this.reachGoalIfNotReach(GOAL_CLICK_PATIENT_DETAILING_ADVANCE_PATIENT_INVOICES);
      this.$router.push(this.navigationToAdvanceList);
    },
    /**
     * @description Обработать нажатие на кнопку пополнения.
     * @returns {void}
     */
    onClickRecharge() {
      this.$emit(NAME_EVENT_OPEN_RECHARGE_FORM);
    },
    /**
     * @description Обработать нажатие снятия с аванса.
     * @returns {void}
     */
    onClickWithdrawal() {
      this.$emit('withdrawal-open');
    },
    onForcePayment() {
      this.$emit(NAME_EVENT_FORCE_RECHARGE);
    },
    onDownloadAct() {
      if (this.isInvalidActDateRange) {
        this.isShowInvalidActDateRangeError = true;
        return;
      }
      const [actDateFrom, actDateTo] = this.actDateRange;
      this.reachGoalIfNotReach(GOAL_PRINT_ACT_MUTUAL_SETTLEMENTS_PATIENT_INVOICES);
      this.$emit('download-act', actDateFrom, actDateTo);
    },
    onPrintAct() {
      if (this.isInvalidActDateRange) {
        this.isShowInvalidActDateRangeError = true;
        return;
      }
      const [actDateFrom, actDateTo] = this.actDateRange;
      this.reachGoalIfNotReach(GOAL_PRINT_ACT_MUTUAL_SETTLEMENTS_PATIENT_INVOICES);
      this.$emit('print-act', actDateFrom, actDateTo);
    },
  },
  props: {
    /**
     * @description Карта лояльности
     * @type {Object|null}
     */
    patientLoyaltyCard: {
      type: Object,
      required: false,
      default: null,
    },
    /**
     * @description Аванс.
     */
    advance: { required: true, type: String },
    /**
     * @description Разрешить принудительно провести оплату.
     */
    canForceRecharge: { default: false, type: Boolean },
    /**
     * @description Оплачено.
     */
    paid: { required: true, type: String },
    /**
     * @description Долг.
     */
    debt: { required: true, type: String },
    /**
     * @description Ожидание завершения запроса на пополнение.
     */
    isAwaitRecharge: { required: true, type: Boolean },
    /**
     * @description Ожидание выполнения принудительного проведения оплаты.
     */
    isAwaitForceRecharge: { default: false, type: Boolean },
    /**
     * @description Ожидание формирования акта.
     */
    isAwaitActRequest: { default: false, type: Boolean },
  },
  mixins: [metricMixin],
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.patient-paid-stats {
  align-items: center;
  background-color: var(--section-background-color);
  display: flex;
  flex-direction: row;
  gap: 20px 1rem;
  padding: 20px;
  &__left {
    display: flex;
    flex-flow: row wrap;
    gap: 20px 50px;
    width: 100%;
  }
  &__group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    &__title {
      color: #90A0B7;
      font-size: 14px;
      line-height: 17px;
    }
    &__value {
      color: #192A3E;
      display: flex;
      flex-direction: row;
      font-weight: 800;
      font-size: 18px;
      gap: .5rem;
      line-height: 21px;
      &.debt {
        color: var(--warning-color);
      }
    }
  }
  &__act-form {
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    &__actions {
      display: flex;
      gap: 10px;
    }
    &__button {
      &.download {
        @include buttons.btn-transparent(100%, 32px, 16px);
      }
      &.print {
        @include buttons.btn-blue(100%, 32px, 16px);
      }
    }
    &__violation {
      color: var(--warning-color);
      font-size: 12px;
      line-height: 14px;
      font-weight: 400;
      padding: 0.125rem;
    }
  }
  &__act {
    @include buttons.btn-transparent(100%, 48px, 16px);
    background-color: transparent;
    max-width: 225px;
    padding: 0;
    margin: 0;
  }
  &__force-recharge {
    padding: 0 10px;
    @include buttons.btn-blue(220px, 40px, 14px);
    &:disabled {
      background-color: var(--unfocus-color);
      border: 1px solid var(--unfocus-color);
      color: white;
    }
  }
  &__button {
    display: block;
    position: relative;
    border: 1px solid var(--action-color);
    border-radius: 4px;
    font-size: 0;
    height: 1.5rem;
    width: 1.5rem;
    &.recharge {
      background: no-repeat center url("@/assets/img/plus.svg");
    }
    &.withdrawal {
      background: no-repeat center url("@/assets/img/minus-rounded-blue.svg");
    }
    &.advance-details-open {
      background: no-repeat center url("@/assets/img/trending-up.svg");
    }
  }
}
@media screen and (max-width: 700px) {
  .patient-paid-stats {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
